import React from 'react';
import { useTheme } from '@mui/material';
import { DashboardCard, DashboardCardSkeleton } from '../../../../../shared/components';
import { DonutChart } from '../../../../../shared/components/charts';
import { AnnotatableChart } from '../../../../../shared/components/charts/AnnotatableChart';
import { WellnessScoreGauge } from './WellnessScoreGauge';
import { useMetricsTrends } from '../../../../../shared/hooks';
import { transformTrendsData } from '../../shared/utils';
import type { MetricsFilters } from '../../../../../shared/api/types';
import type { DistributionEntry, RecoveryTrendMetric } from '../api/types';

interface RecoveryGaugeProps {
  isLoading: boolean;
  weatherFilters: MetricsFilters;
  showBreakdown?: boolean;
  breakdownOpen?: boolean;
  onToggleBreakdown?: () => void;
}

export const RecoveryGauge: React.FC<RecoveryGaugeProps> = ({
  isLoading,
  weatherFilters,
  showBreakdown,
  breakdownOpen,
  onToggleBreakdown,
}) => {
  if (isLoading) {
    return <DashboardCardSkeleton height={200} />;
  }

  return (
    <WellnessScoreGauge
      metric='recovery'
      filterParams={weatherFilters}
      showBreakdown={showBreakdown}
      breakdownOpen={breakdownOpen}
      onToggleBreakdown={onToggleBreakdown}
    />
  );
};

interface RecoveryDistributionProps {
  distribution: DistributionEntry[] | undefined;
  isLoading: boolean;
}

export const RecoveryDistribution: React.FC<RecoveryDistributionProps> = ({ distribution, isLoading }) => {
  const theme = useTheme();

  if (isLoading) {
    return <DashboardCardSkeleton height={200} />;
  }

  const getRecoveryColor = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes('67-100') || n.includes('high') || n === 'green') return theme.palette.success.main;
    if (n.includes('34-66') || n.includes('medium') || n === 'yellow') return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const distributionData = (distribution ?? []).map((d) => ({
    label: d.name,
    value: d.value,
    color: getRecoveryColor(d.name),
  }));

  return (
    <DashboardCard
      title='Recovery Score Distribution'
      subtitle='Proportion of employees in each recovery range.'
      expandable
      expandedContent={<DonutChart data={distributionData} height={350} />}
    >
      <DonutChart data={distributionData} height={220} />
    </DashboardCard>
  );
};

interface RecoveryTrendProps {
  deptName: string;
  clientId: string | null;
  weatherFilters: MetricsFilters;
  selectedMetric: RecoveryTrendMetric;
}

const METRIC_CONFIG: Record<RecoveryTrendMetric, { label: string; color: string; yAxisMax: number }> = {
  recovery_score: { label: 'Recovery Score', color: '#4caf50', yAxisMax: 100 },
  hrv: { label: 'Heart Rate Variability', color: '#2196f3', yAxisMax: 200 },
  resting_heart_rate: { label: 'Resting Heart Rate', color: '#ff9800', yAxisMax: 100 },
  sleep_performance: { label: 'Sleep Performance', color: '#9c27b0', yAxisMax: 100 },
};

export const RecoveryTrend: React.FC<RecoveryTrendProps> = ({ deptName, clientId, weatherFilters, selectedMetric }) => {
  const trendsQuery = useMetricsTrends({
    ...weatherFilters,
    metrics: ['recovery_score', 'resting_heart_rate', 'hrv', 'sleep_performance'],
    granularity: 'daily',
  });

  const trendData = transformTrendsData(trendsQuery.data, [selectedMetric]);
  const config = METRIC_CONFIG[selectedMetric];

  return (
    <DashboardCard
      title='Recovery Trends'
      subtitle={`${deptName} ${config.label.toLowerCase()} over time`}
      expandable
      expandedContent={
        <AnnotatableChart
          clientId={clientId}
          startDate={weatherFilters.start_date}
          endDate={weatherFilters.end_date}
          weatherFilters={weatherFilters}
          data={trendData}
          xAxisKey='label'
          series={[{ dataKey: selectedMetric, label: config.label, color: config.color, area: true }]}
          yAxisMin={0}
          yAxisMax={config.yAxisMax}
          height={500}
        />
      }
    >
      <AnnotatableChart
        clientId={clientId}
        startDate={weatherFilters.start_date}
        endDate={weatherFilters.end_date}
        weatherFilters={weatherFilters}
        data={trendData}
        xAxisKey='label'
        series={[{ dataKey: selectedMetric, label: config.label, color: config.color, area: true }]}
        yAxisMin={0}
        yAxisMax={config.yAxisMax}
        height={250}
      />
    </DashboardCard>
  );
};
